import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export type TaxTreatment = 'taxable' | 'nil_rated' | 'exempt' | 'non_gst'

export const TAX_TREATMENT_LABELS: Record<TaxTreatment, string> = {
  taxable: 'Taxable',
  nil_rated: 'Nil-rated',
  exempt: 'Exempt',
  non_gst: 'Non-GST',
}

/** A tax slab: a name, a treatment and ONE headline GST rate. The split below is derived by the API, never typed. */
export interface TaxMaster {
  id: string
  organizationId: string
  code: string
  name: string
  treatment: TaxTreatment
  /** Total GST percentage (0 unless taxable). */
  rate: number
  /** Derived: half of the rate (central). */
  cgstRate: number
  /** Derived: half of the rate (State, or UT in a Union Territory without a legislature). */
  sgstRate: number
  /** Derived: the whole rate, charged alone on a sale across State lines. */
  igstRate: number
  effectiveFrom: string | undefined
  effectiveTo: string | undefined
  notificationRef?: string
  helpText?: string
  isActive: boolean
  isSystem?: boolean
  /** How many services and HSN/SAC codes use this slab. */
  usageCount?: number
  /** Plain-language line, e.g. "Within a State: CGST 9% + SGST 9%. Across States: IGST 18%". */
  preview?: string
  createdAt: string
  updatedAt: string
}

/** What a dropdown shows for a slab: its name, plus the rate when the name does not already say it. */
export function taxLabel(tax: Pick<TaxMaster, 'name' | 'treatment' | 'rate'>): string {
  return tax.treatment === 'taxable' && !tax.name.includes(String(tax.rate)) ? `${tax.name} (${tax.rate}%)` : tax.name
}

export interface TaxMasterFilters {
  treatment?: TaxTreatment
  isActive?: boolean
  search?: string
}

export function useTaxMasters(filters?: TaxMasterFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['taxMasters', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.treatment) params.set('treatment', filters.treatment)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      const res = await api.get<any>(`/master-data/taxes?${params}`)
      return (res?.data || []) as TaxMaster[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useTaxMasterMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['taxMasters', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<TaxMaster, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/taxes', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TaxMaster> }) =>
      api.patch<any>(`/master-data/taxes/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/taxes/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/taxes/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove }
}